import { faExternalLink, faTimes, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import {
  BillingItem,
  BillingItemFieldEdit,
  DetectedAlert,
  EncounterChart
} from '@/types/encounter';
import DataList, { DataRow } from '@/ui/components/DataList';
import DualTime from '@/ui/components/DualTime';
import Heading from '@/ui/components/Heading';
import Tooltip from '@/ui/components/Tooltip';
import { currencyFormatter } from '@/utils/currencies';
import formatStaffName from '@/utils/formatStaffName';
import { titleize } from '@/utils/titleize';

import EncounterCharts from '../EncounterCharts';

const BillingDetails = ({
  billingItem,
  charts = []
}: {
  billingItem: BillingItem;
  charts?: EncounterChart[];
}) => {
  const { host } = useContext(AppContext);
  const row = billingItem.row;

  const {
    healthie_claim_rows: claim_rows,
    forced_valid_at,
    forced_valid_by,
    manually_associated_at,
    manually_associated_by,
    created_at
  } = billingItem;

  const groupedAlerts: Record<string, DetectedAlert[]> = billingItem.active_detected_alerts?.reduce(
    (acc, alert) => {
      if (!acc[alert.group]) {
        acc[alert.group] = [];
      }
      acc[alert.group].push(alert);
      return acc;
    },
    {} as Record<string, DetectedAlert[]>
  );

  const getFieldEditValue = (fieldEdit: BillingItemFieldEdit) => {
    if (fieldEdit.edit_type == 'mark_unbillable') {
      return billingItem.non_billable_reason?.name || 'No reason provided';
    }
    return Array.isArray(fieldEdit.value) ? fieldEdit.value.join(', ') : fieldEdit.value;
  };
  return (
    <>
      {row && (
        <>
          {/* Service Information */}
          {(row.service_type || row.service_date || row.service_class || row.service_location) && (
            <>
              <div className="mt-4 pt-2 ">
                <Heading value={3} text="Service Information" />
                <DataList>
                  {row.service_type && <DataRow label="Service Type" content={row.service_type} />}
                  {row.service_date && (
                    <DataRow
                      label="Service Date"
                      content={<DualTime dateTime={new Date(row.service_date)} />}
                    />
                  )}
                  {row.service_class && (
                    <DataRow label="Service Class" content={row.service_class} />
                  )}
                  {row.service_location && (
                    <DataRow label="Service Location" content={row.service_location} />
                  )}
                </DataList>
              </div>
            </>
          )}
        </>
      )}

      <EncounterCharts charts={charts} />

      {billingItem.active_detected_alerts?.length > 0 && (
        <div className="mt-4 border-t pt-2 border-y-gray-200 border-solid">
          <Heading value={3} text="Alerts" />
          {Object.entries(groupedAlerts).map(([group, alerts]: [string, DetectedAlert[]]) => (
            <div key={group} className="my-2">
              <Heading value={4} text={titleize(group)} />
              {alerts.map((alert: DetectedAlert) => (
                <div key={alert.id} className="my-1">
                  <div className="flex items-center ml-2">
                    {alert.level === 'error' ? (
                      <FontAwesomeIcon icon={faTimes} className="text-red-500" />
                    ) : (
                      <FontAwesomeIcon icon={faWarning} className="text-yellow-500" />
                    )}
                    <span className="ml-2">{alert.description}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {billingItem.flags?.length > 0 && (
        <div className="mt-4 border-t pt-2 border-y-gray-200 border-solid">
          <Heading value={3} text="Flags" />
          <ol>
            {billingItem.flags.map((flag) => (
              <li key={flag.id} className="border-b border-gray-100 py-2">
                <Tooltip
                  tooltipContent={
                    <>
                      <p>{flag.reason && `Reason: ${flag.reason}`}</p>
                      <p>{flag.notes ? `: ${flag.notes}` : <i>No Note</i>}</p>
                    </>
                  }>
                  {titleize(flag.type)}
                  <br />
                  {flag.ignored_alerts && flag.ignored_alerts?.length > 0 && (
                    <div className="text-sm">
                      {flag.ignored_alerts
                        ?.map((alert) => `${titleize(alert.group)}: ${alert.description}`)
                        .join(', ') || 'None'}
                    </div>
                  )}
                  <span className="text-sm leading-5 text-gray-500">
                    {flag.by && ` by ${formatStaffName({ staff: flag.by })}`} on{' '}
                    <DualTime dateTime={new Date(flag.at)} />
                  </span>
                </Tooltip>
              </li>
            ))}
          </ol>
        </div>
      )}
      {billingItem.billing_item_edits?.length > 0 && (
        <div className="mt-4 border-t pt-2 border-y-gray-200 border-solid">
          <Heading value={3} text="Edits" />
          {billingItem.billing_item_edits.map((edit) => (
            <div key={edit.id} className="my-2">
              <ul>
                {edit.field_edits.map((fieldEdit) => (
                  <li key={fieldEdit.id}>
                    {titleize(fieldEdit.edit_type)}:{getFieldEditValue(fieldEdit)}
                    <br />
                  </li>
                ))}
              </ul>
              <p className="text-sm leading-5 text-gray-500">
                {edit.notes || 'No notes'} by {formatStaffName({ staff: edit.by })} on{' '}
                <DualTime dateTime={new Date(edit.at)} />
              </p>
            </div>
          ))}
        </div>
      )}
      {billingItem.billing_report?.id && (
        <div className="mt-4 border-t pt-2 border-y-gray-200 border-solid">
          <a
            href={`${host}/billing/reports/${billingItem.billing_report.id}`}
            className="text-blue-600"
            target="_blank"
            rel="noreferrer">
            Billing report in homebase
            <FontAwesomeIcon className="ml-1" icon={faExternalLink} />
          </a>
        </div>
      )}
      {claim_rows?.length > 0 && (
        <div className="mt-4 border-t pt-2 border-y-gray-200 border-solid">
          {claim_rows?.map((claim_row) => {
            const mods = claim_row
              ? [claim_row.mod1, claim_row.mod2, claim_row.mod3, claim_row.mod4]
              : [];
            const shouldDisplayMods = mods.some((mod) => mod !== '');

            return (
              <DataList key={claim_row.id}>
                {claim_row && (
                  <DataRow
                    label="Code"
                    content={`${claim_row.cpt_code_code}: ${claim_row.cpt_code_description}`}
                  />
                )}
                {shouldDisplayMods && (
                  <DataRow label="Modifiers" content={mods.filter((mod) => mod).join(', ')} />
                )}
                {claim_row?.fee_in_cents !== undefined && (
                  <DataRow
                    label="Fee"
                    content={`${currencyFormatter((claim_row.fee_in_cents || 0) / 100)} x ${
                      claim_row.units
                    }`}
                  />
                )}
                {created_at && (
                  <DataRow
                    label="Created at"
                    content={<DualTime dateTime={new Date(created_at)} />}
                  />
                )}
              </DataList>
            );
          })}
        </div>
      )}

      {forced_valid_at && forced_valid_by?.first_name && (
        <p className="text-xs leading-5 text-gray-500 mt-2">
          Forced valid by {formatStaffName({ staff: forced_valid_by })} on{' '}
          <DualTime dateTime={new Date(forced_valid_at)} />
        </p>
      )}

      {manually_associated_at && manually_associated_by?.first_name && (
        <p className="text-xs leading-5 text-gray-500 mt-2">
          Manually associated by {formatStaffName({ staff: manually_associated_by })} on{' '}
          <DualTime dateTime={new Date(manually_associated_at)} />
        </p>
      )}
    </>
  );
};

export default BillingDetails;
